// text/html is deliberately excluded — it's the one format here whose raw
// content is "renders as live markup" rather than inert data, so it's the
// single riskiest thing to keep accepting into a client-side pipeline
// (composer file preview, per-message attachment payload) as new code
// touches it over time, for zero corresponding ICP benefit: Mora's users
// (content creators, not developers) already have a first-class way to
// reference a webpage — paste the URL, which the composer fetches and
// summarizes — nobody in this ICP is saving a page's HTML source and
// attaching that file. Reviewed 2026-07-21; see
// docs/superpowers/specs/2026-07-21-file-upload-type-review.md.
export const ALLOWED_FILE_MIMES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'text/plain',
  'text/markdown',
] as const;

export type AllowedMime = (typeof ALLOWED_FILE_MIMES)[number];

const MARKDOWN_EXTENSIONS = new Set(['.md', '.markdown', '.mdown', '.mkd']);
const GENERIC_MARKDOWN_MIMES = new Set([
  '',
  'application/octet-stream',
  'application/x-markdown',
  'text/plain',
  'text/x-markdown',
]);
const GENERIC_FILE_MIMES = new Set(['', 'application/octet-stream']);
const MIME_BY_EXTENSION: Partial<Record<string, AllowedMime>> = {
  '.pdf': 'application/pdf',
  '.txt': 'text/plain',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
  '.heic': 'image/heic',
  '.heif': 'image/heif',
};

function fileExtension(name: string | undefined): string {
  if (!name) return '';
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : '';
}

/** Normalize the browser-specific MIME variants used for local Markdown files. */
export function resolveAllowedFileMime(file: { type?: string; name?: string }): AllowedMime | null {
  const declared = file.type?.toLowerCase() ?? '';
  if (MARKDOWN_EXTENSIONS.has(fileExtension(file.name)) && GENERIC_MARKDOWN_MIMES.has(declared)) {
    return 'text/markdown';
  }
  if (GENERIC_FILE_MIMES.has(declared)) {
    return MIME_BY_EXTENSION[fileExtension(file.name)] ?? null;
  }
  return isAllowedMime(declared) ? declared : null;
}

export const MAX_FILE_BYTES = 20 * 1024 * 1024;
export const MAX_TOTAL_MESSAGE_BYTES = 50 * 1024 * 1024;
export const MAX_FILES_PER_MESSAGE = 6;

export function isAllowedMime(mime: string | undefined | null): mime is AllowedMime {
  if (!mime) return false;
  return (ALLOWED_FILE_MIMES as readonly string[]).includes(mime);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export interface FileValidationResult {
  ok: boolean;
  reason?: string;
}

export function validateFile(file: {
  type?: string;
  size?: number;
  name?: string;
}): FileValidationResult {
  if (!resolveAllowedFileMime(file)) {
    return {
      ok: false,
      reason: `Unsupported type ${file.type || 'unknown'}. Allowed: PDF, images, plain text, markdown.`,
    };
  }
  if ((file.size ?? 0) > MAX_FILE_BYTES) {
    return {
      ok: false,
      reason: `${file.name ?? 'File'} is too large (${formatBytes(file.size ?? 0)}). Max ${formatBytes(MAX_FILE_BYTES)} per file.`,
    };
  }
  return { ok: true };
}

export function estimateDataUrlBytes(dataUrl: string): number {
  const commaIdx = dataUrl.indexOf(',');
  if (commaIdx < 0) return dataUrl.length;
  const b64 = dataUrl.slice(commaIdx + 1);
  return Math.ceil((b64.length * 3) / 4);
}

export async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') resolve(result);
      else reject(new Error('Expected string result from FileReader'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });
}
